import type { ApiBucket, ApiMetric } from '../apiTypes';
import type { DistributionItem, Kpi, MetricListItem } from '../chartTypes';
import { type BucketConfig, type MetricListBucketConfig, UNKNOWN_BUCKET_KEY } from '../constants';

/** Building blocks shared by the per-chart parsers — prefer extending these over duplicating logic in a parser */

export const toCount = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

export const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

export const toPercent = (part: number, total: number): number =>
  total > 0 ? roundToOneDecimal((part / total) * 100) : 0;

export const parseKpi = (metric: ApiMetric | undefined): Kpi => {
  const value = typeof metric?.value === 'number' && Number.isFinite(metric.value) ? metric.value : null;
  const previous = metric?.previous_value;
  const change =
    value !== null && typeof previous === 'number' && Number.isFinite(previous) && previous > 0
      ? roundToOneDecimal(((value - previous) / previous) * 100)
      : null;

  return { value, change };
};

/** Maps API buckets onto the configured buckets (in config order); unrecognised buckets count as "Unknown" */
export const parseDistribution = (buckets: ApiBucket[] | undefined, config: BucketConfig[]): DistributionItem[] => {
  if (!buckets?.length) {
    return [];
  }

  const knownKeys = new Set(config.map(({ key }) => key));
  const counts = new Map<string, number>();

  for (const { bucket, count } of buckets) {
    const key = knownKeys.has(bucket) ? bucket : UNKNOWN_BUCKET_KEY;
    counts.set(key, (counts.get(key) ?? 0) + toCount(count));
  }

  const total = [...counts.values()].reduce((sum, count) => sum + count, 0);

  return config.map(({ key, label, color }) => {
    const value = counts.get(key) ?? 0;
    return { key, label, color, value, share: toPercent(value, total) };
  });
};

/**
 * Maps API buckets onto the configured rows without computing shares — for non-exclusive segments.
 * Rows are sorted by value (highest first) and zero-value rows are dropped; unrecognised buckets are ignored.
 * Configured rows missing from the response get a `null` value and go last; ties keep config order.
 */
export const parseMetricList = (
  buckets: ApiBucket[] | undefined,
  config: MetricListBucketConfig[]
): MetricListItem[] => {
  if (!buckets?.length) {
    return [];
  }

  const counts = new Map<string, number>();
  for (const { bucket, count } of buckets) {
    counts.set(bucket, (counts.get(bucket) ?? 0) + toCount(count));
  }

  return config
    .map(({ key, label, icon }) => ({ key, label, icon, value: counts.get(key) ?? null }))
    .filter(({ value }) => value !== 0)
    .sort((a, b) => (b.value ?? -1) - (a.value ?? -1));
};
